import { useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { Trans } from "react-i18next";
import classNames from "classnames";
import { useAppDispatch } from "@hooks/redux";
import { fetchProductList } from "@pages/library/librarySlice";
import { formatProductData } from "@pages/aiStudio/components/ProductLibraryModal";
import { formatImgUrl } from "@utils/format";
import Image from "@components/Image";
import TruncateTitle from "@components/TruncateTitle";

interface SavedProductRecord {
  id: string;
  productTitle: string;
  productCategory?: string;
  coverImageUrl?: string;
  country?: string;
  priCateName?: string | null;
  secCateName?: string | null;
  terCateName?: string | null;
}

export interface SavedProductPickerProps {
  /** 选中后立即回填编辑器 */
  onSelect: (product: SavedProductRecord) => void;
}

/**
 * 「Created via Saved」入口：从用户收藏的 Kalodata 商品列表里选一个。
 *
 * 设计稿节点：3433:24033
 */
export const SavedProductPicker = ({ onSelect }: SavedProductPickerProps) => {
  const dispatch = useAppDispatch();
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchProductList())
      .then((result: any) => {
        setList(result.payload ?? []);
      })
      .catch((err) => console.error("Failed to fetch product library list:", err))
      .finally(() => setLoading(false));
  }, [dispatch]);

  const records = useMemo<SavedProductRecord[]>(
    () => list.map((item) => formatProductData(item) as SavedProductRecord),
    [list],
  );

  return (
    <Spin spinning={loading}>
      <div className="max-h-[60vh] min-h-[300px] overflow-y-auto kds-scroll-thin -mr-[8px] pr-[8px] pt-[8px]">
        {records.length === 0 && !loading ? (
          <div className="flex items-center justify-center h-[300px] text-kds-fg-soft text-[14px]">
            <Trans>Nothing here yet.</Trans>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-[12px]">
            {records.map((record) => {
              const isSelected = selectedId === record.id;
              return (
                <div key={record.id} className="relative">
                  <div
                    className="cursor-pointer bg-kds-bg-card border border-kds-border rounded-[8px] overflow-hidden hover:border-kds-accent transition-colors"
                    onClick={() => {
                      setSelectedId(record.id);
                      onSelect(record);
                    }}
                  >
                    <Image
                      url={record.coverImageUrl || formatImgUrl("product", record.id)}
                      className="w-full aspect-square object-cover border-none! rounded-none!"
                    />
                    <div className="px-[8px] py-[10px]">
                      <TruncateTitle
                        className="text-[13px] text-kds-fg leading-[18px] h-[36px]"
                        text={record.productTitle}
                        lineclamp2
                        placement="top"
                      />
                    </div>
                  </div>
                  <div
                    className={classNames(
                      "absolute z-10 -top-[2px] -right-[2px] -bottom-[2px] -left-[2px] border-2 rounded-[10px] pointer-events-none transition-colors",
                      isSelected ? "border-kds-accent!" : "border-transparent",
                    )}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Spin>
  );
};
